import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { EligibilityPill } from "@/components/EligibilityPill";
import { MatchScore } from "@/components/MatchScore";
import { FreshnessBadge } from "@/components/FreshnessBadge";
import { formatSalary, locationLabel, arrangementLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, BookmarkSimple, ArrowSquareOut, CheckCircle, XCircle, WarningCircle,
  Question, CaretDown, Briefcase, WarningDiamond, ArrowsLeftRight, Flag,
} from "@phosphor-icons/react";
import ApplicationDrawer from "@/components/ApplicationDrawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DimStatusChip, dimStatusFor, CriteriaFitCaption, HumanAgencyNote } from "@/components/FitTransparency";
import { RemoteScopePanel, SourceEvidence } from "@/components/RemoteScope";

const CHECK_ICON = {
  eligible: { Icon: CheckCircle, cls: "text-emerald-600" },
  possibly_eligible: { Icon: WarningCircle, cls: "text-orange-500" },
  not_eligible: { Icon: XCircle, cls: "text-red-600" },
};

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [appOpen, setAppOpen] = useState(false);

  const load = () => api.get(`/jobs/${id}`)
    .then((r) => { setData(r.data); setSaved(r.data.saved); })
    .catch(() => setErr(true));
  useEffect(() => { load(); }, [id]);

  const toggleSave = async () => {
    try {
      if (saved) { await api.delete(`/saved-jobs/${id}`); setSaved(false); toast("Removed from saved"); }
      else { await api.post(`/saved-jobs/${id}`); setSaved(true); toast.success("Saved"); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (err) return (
    <div className="max-w-md mx-auto text-center py-16" data-testid="job-unavailable">
      <p className="font-heading font-bold text-lg text-slate-900">Job not found or unavailable</p>
      <p className="mt-1 text-sm text-slate-500">This role may have been closed, or you don't have access to it.</p>
      <button onClick={() => navigate(-1)} className="mt-4 text-sm text-slate-700 underline">Go back</button>
    </div>
  );
  if (!data) return <p className="text-slate-400 text-sm">Loading…</p>;
  const { job, eligibility, match, application, freshness, contradictions } = data;
  const preferenceFit = data.preference_fit;
  const practicalFit = data.practical_fit;
  const flexAreas = new Set((match.flex_used || []).map((f) => f.area));
  const remoteSummary = data.remote_summary;
  const sourceEvidence = data.source_evidence;
  const overall = data.overall;
  const summary = data.summary;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-btn">
        <ArrowLeft /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
        {/* LEFT — job facts */}
        <div className="space-y-6">
          {job.is_active === false && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="job-inactive-banner">
              This job is no longer active. It won't appear in new searches or matches, but it stays here in your history.
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900" data-testid="job-title">{job.title}</h1>
                <p className="mt-1 text-slate-600">{job.employer}{job.recruiter ? ` · via ${job.recruiter}` : ""}</p>
              </div>
              <Button variant="outline" size="icon" onClick={toggleSave} data-testid="save-toggle-btn" className={saved ? "border-slate-900" : "border-slate-300"}>
                <BookmarkSimple weight={saved ? "fill" : "regular"} className={saved ? "text-slate-900" : "text-slate-500"} />
              </Button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <FreshnessBadge freshness={freshness} showActive />
              <Badge variant="secondary" className="font-normal">{locationLabel(job)}</Badge>
              <Badge variant="secondary" className="font-normal">{arrangementLabel(job.work_arrangement)}</Badge>
              {job.employment_type && <Badge variant="secondary" className="font-normal">{job.employment_type}</Badge>}
              {formatSalary(job.salary_min, job.salary_max, job.salary_currency) && (
                <Badge variant="secondary" className="font-normal font-mono-num">{formatSalary(job.salary_min, job.salary_max, job.salary_currency)}</Badge>
              )}
              {job.travel_requirement && <Badge variant="secondary" className="font-normal">Travel: {job.travel_requirement}</Badge>}
            </div>

            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Row label="Remote from" value={(job.remote_countries || []).join(", ") || (job.work_arrangement === "remote" ? "Not specified" : "—")} />
              <Row label="Remote scope" value={job.remote_scope ? (job.remote_scope === "region" && job.remote_region ? `${job.remote_scope} · ${job.remote_region}` : job.remote_scope) : null} />
              <Row label="Office attendance" value={job.office_frequency || (job.office_days_per_week != null ? `${job.office_days_per_week} day(s)/week` : null)} />
              <Row label="Timezone" value={job.timezone || (job.timezone_offset != null ? `UTC${job.timezone_offset >= 0 ? "+" : ""}${job.timezone_offset}` : null)} />
              <Row label="Travel" value={job.travel_frequency} />
              <Row label="Employment model" value={job.employment_mechanism} />
              <Row label="Location restriction reason" value={job.location_rationale ? job.location_rationale.replace(/_/g, " ") : null} />
              <Row label="Relocation" value={job.relocation_required || "unknown"} />
              <Row label="Working hours" value={job.working_hours} />
              <Row label="Work authorization" value={job.work_authorization_country} />
              <Row label="Experience" value={job.experience_years ? `${job.experience_years}+ years` : job.experience_level} />
              <Row label="Education" value={job.education_requirement} />
              <Row label="Application deadline" value={job.application_deadline} />
              <Row label="Application method" value={job.application_method} />
              <Row label="Video required" value={job.mandatory_video ? "Yes — mandatory video" : "No"} />
              <Row label="Licence required" value={job.required_license} />
            </dl>

            {(job.required_languages || []).length > 0 && (
              <div className="mt-5">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-1.5">Required languages</p>
                <div className="flex flex-wrap gap-2">
                  {job.required_languages.map((l, i) => (
                    <Badge key={i} variant="outline" className="font-normal">{l.language} · {l.level}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <RemoteScopePanel summary={remoteSummary} />
          <ReqList title="Mandatory qualifications" items={job.mandatory_qualifications} tone="mandatory" />
          <ReqList title="Preferred qualifications" items={job.preferred_qualifications} tone="preferred" />
          <ReqList title="Flexible qualifications" items={job.flexible_qualifications} tone="preferred" />

          {job.description && (
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-heading font-bold text-lg mb-3">Job description <span className="text-xs font-sans font-normal text-slate-400">(original employer text)</span></h2>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{job.description}</p>
            </div>
          )}

          {/* Original listing / source */}
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg">Original listing</h2>
              {job.source_url && (
                <a href={job.source_url} target="_blank" rel="noopener noreferrer" data-testid="source-link"
                  className="text-sm font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1.5">
                  View source <ArrowSquareOut />
                </a>
              )}
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Source: {job.source_attribution && job.source_url
                ? <a href={job.source_url} target="_blank" rel="noopener" className="underline" data-testid="source-attribution-link">{job.source}</a>
                : (job.source || "Unknown")} · Published: {job.publication_date || "unknown"} · Last verified: {job.last_verified_date || "unknown"}
            </p>
            {job.source_attribution && (
              <p className="mt-1 text-xs text-slate-500" data-testid="source-attribution-note">
                Listing via {job.source}. Apply on the original listing; Eligvia does not re-host or forward this job.
              </p>
            )}
            {job.source_text && (
              <div className="mt-3">
                <button onClick={() => setShowSource((s) => !s)} className="text-sm text-slate-600 flex items-center gap-1" data-testid="toggle-source-text">
                  <CaretDown className={`transition-transform ${showSource ? "rotate-180" : ""}`} /> {showSource ? "Hide" : "Show"} original source text
                </button>
                {showSource && <p className="mt-2 text-xs text-slate-500 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-md p-3 max-h-64 overflow-y-auto">{job.source_text}</p>}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — analysis (sticky) */}
        <div className="lg:sticky lg:top-6 space-y-4">
          {contradictions && contradictions.length > 0 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-5" data-testid="contradictions-panel">
              <p className="flex items-center gap-2 text-sm font-semibold text-orange-900">
                <WarningDiamond weight="fill" className="text-orange-500" /> Check carefully — possible contradictions
              </p>
              <ul className="mt-2 space-y-2">
                {contradictions.map((c, i) => (
                  <li key={i} className="text-sm text-orange-800" data-testid="contradiction-item">
                    <span className="font-medium">{c.title}:</span> {c.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {freshness && freshness.status !== "active" && freshness.note && (
            <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-start gap-2" data-testid="freshness-note">
              <FreshnessBadge freshness={freshness} showActive />
              <p className="text-sm text-slate-600 leading-relaxed">{freshness.note}</p>
            </div>
          )}

          <OverallPanel overall={overall} eligibility={eligibility} />
          <FitSummaryPanel summary={summary} />

          <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="eligibility-panel">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">Your eligibility <span className="normal-case tracking-normal text-slate-400 font-normal">· hard rules, checked first</span></p>
            <EligibilityPill status={eligibility.status} size="lg" />
            <div className="mt-4 space-y-3">
              {eligibility.checks.length === 0 && <p className="text-sm text-slate-500">No specific eligibility conditions found in this advert.</p>}
              {eligibility.checks.map((c, i) => {
                const { Icon, cls } = CHECK_ICON[c.status] || CHECK_ICON.possibly_eligible;
                const ds = dimStatusFor(c, flexAreas);
                return (
                  <div key={i} className="flex gap-2.5" data-testid={`elig-check-${c.status}`}>
                    <Icon weight="fill" className={`${cls} mt-0.5 shrink-0`} size={18} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-800">{c.title}</p>
                        <div className="flex items-center gap-1.5">
                          <ReqTypeBadge type={c.req_type} />
                          <DimStatusChip status={ds} />
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">{c.explanation}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="match-panel">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Capability fit <span className="normal-case tracking-normal text-slate-400 font-normal">· can you do the job</span></p>
              <MatchScore score={match.score} size="lg" />
            </div>
            <CriteriaFitCaption className="mt-1.5" />
            {eligibility.status === "not_eligible" && (
              <p className="mt-2 text-xs text-red-600">This role has a hard blocker (see eligibility above). Criteria fit is shown for reference only — eligibility always comes first, and a fit score never overrides a blocker.</p>
            )}

            {match.strong_matches.length > 0 && (
              <Analysis title="Strong matches" tone="good" items={match.strong_matches.map((m) => ({ text: m.text, note: m.note }))} />
            )}
            {match.flex_used && match.flex_used.length > 0 && (
              <div className="mt-4" data-testid="flex-used-panel">
                <p className="text-sm font-semibold text-blue-700 flex items-center gap-1.5"><ArrowsLeftRight weight="bold" className="text-blue-400" /> Flexibility used</p>
                <ul className="mt-1.5 space-y-1.5">
                  {match.flex_used.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-blue-800" data-testid="flex-used-item">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span><span className="font-medium">{f.area}</span> — {f.note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {match.gaps.length > 0 && (
              <Analysis title="Potential gaps" tone="neutral" items={match.gaps.map((g) => ({ text: g.text, note: g.note }))} />
            )}
            {match.blockers.length > 0 && (
              <Analysis title="Potential blockers" tone="bad" items={match.blockers.map((b) => ({ text: b.text, note: b.note }))} />
            )}
            {match.missing_info.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Question weight="bold" className="text-slate-400" /> Missing information</p>
                <ul className="mt-1.5 space-y-1">
                  {match.missing_info.map((m, i) => <li key={i} className="text-sm text-slate-500 leading-relaxed">— {m}</li>)}
                </ul>
              </div>
            )}
            <HumanAgencyNote className="mt-4" />
          </div>

          <FitLayers preference={preferenceFit} practical={practicalFit} />

          <SourceEvidence evidence={sourceEvidence} />

          <div className="flex flex-col gap-2">
            <Button onClick={() => setAppOpen(true)} data-testid="prepare-application-btn" className="w-full bg-slate-900 hover:bg-slate-800 gap-2">
              <Briefcase weight="fill" /> {application ? "Manage application" : "Prepare application"}
            </Button>
            {job.source_url && (
              <a href={job.source_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full border-slate-300 gap-2">Apply on source <ArrowSquareOut /></Button>
              </a>
            )}
            <ReportDialog jobId={job.id} />
          </div>
        </div>
      </div>

      <ApplicationDrawer open={appOpen} onOpenChange={setAppOpen} job={job} application={application} onSaved={load} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="text-slate-800">{value || <span className="text-slate-400">Unknown</span>}</dd>
    </div>
  );
}

function ReqList({ title, items, tone }) {
  if (!items || items.length === 0) return null;
  const dot = tone === "mandatory" ? "bg-slate-800" : "bg-slate-300";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="font-heading font-bold text-lg mb-3">{title}</h2>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${dot} shrink-0`} /> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Analysis({ title, tone, items }) {
  const meta = {
    good: "text-emerald-700",
    neutral: "text-slate-700",
    bad: "text-red-700",
  }[tone];
  const dot = { good: "bg-emerald-500", neutral: "bg-slate-300", bad: "bg-red-500" }[tone];
  return (
    <div className="mt-4">
      <p className={`text-sm font-semibold ${meta}`}>{title}</p>
      <ul className="mt-1.5 space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${dot} shrink-0`} />
            <span>{it.text}{it.note ? <span className="text-slate-400"> — {it.note}</span> : null}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


const FIT_DOT = { met: "bg-emerald-500", partial: "bg-amber-500", mismatch: "bg-red-500", unknown: "bg-slate-300" };
const FIT_BADGE = {
  good: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  weak: "bg-red-50 text-red-700 border-red-200",
  unknown: "bg-slate-50 text-slate-500 border-slate-200",
};

function FitBlock({ title, subtitle, data, testid }) {
  if (!data) return null;
  const badge = FIT_BADGE[data.status] || FIT_BADGE.unknown;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          <p className="text-xs text-slate-400">{subtitle}</p>
        </div>
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge}`} data-testid={`${testid}-status`}>
          {data.score != null ? `${data.score}% · ${data.status}` : "unknown"}
        </span>
      </div>
      <ul className="mt-3 space-y-1.5">
        {(data.factors || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${FIT_DOT[f.status] || FIT_DOT.unknown}`} />
            <span className="text-slate-700 flex-1">{f.label}: <span className="text-slate-500">{f.why}</span></span>
            <ReqTypeBadge type={f.req_type} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function FitLayers({ preference, practical }) {
  if (!preference && !practical) return null;
  return (
    <div className="space-y-4" data-testid="fit-layers">
      <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
        Personal fit <span className="normal-case tracking-normal text-slate-400 font-normal">· separate from eligibility — never overrides a hard rule</span>
      </p>
      <FitBlock title="Preference fit" subtitle="Does this match what you want?" data={preference} testid="preference-fit-panel" />
      <FitBlock title="Practical fit" subtitle="Does this work for your situation?" data={practical} testid="practical-fit-panel" />
    </div>
  );
}

function OverallPanel({ overall, eligibility }) {
  if (!overall) return null;
  const blocked = overall.status === "blocked";
  const noScore = overall.score == null;
  const ring = blocked ? "border-red-200 bg-red-50" : overall.provisional ? "border-amber-200 bg-amber-50" : "border-slate-900 bg-slate-900 text-white";
  const sub = blocked ? "text-red-700" : overall.provisional ? "text-amber-700" : "text-slate-300";
  return (
    <div className={`rounded-lg border p-6 ${ring}`} data-testid="overall-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className={`text-xs uppercase tracking-wide font-medium ${blocked || overall.provisional ? "text-slate-500" : "text-slate-300"}`}>Overall match</p>
          <p className={`font-heading font-bold text-lg ${blocked ? "text-red-800" : overall.provisional ? "text-amber-800" : "text-white"}`} data-testid="overall-label">{overall.label}</p>
        </div>
        <div className="text-right">
          {noScore
            ? <span className={`font-heading font-bold text-2xl ${blocked ? "text-red-700" : "text-slate-400"}`} data-testid="overall-score">—</span>
            : <span className="font-heading font-bold text-3xl font-mono-num" data-testid="overall-score">{overall.score}<span className="text-lg">%</span>{overall.provisional ? <span className="text-amber-600 text-xl">*</span> : null}</span>}
        </div>
      </div>
      {overall.components && (overall.components.capability != null || overall.components.practical != null || overall.components.preference != null) && !blocked && (
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <Component label="Capability" weight="50%" value={overall.components.capability} dark={!overall.provisional} />
          <Component label="Practical" weight="30%" value={overall.components.practical} dark={!overall.provisional} />
          <Component label="Preference" weight="20%" value={overall.components.preference} dark={!overall.provisional} />
        </div>
      )}
      <p className={`mt-3 text-xs leading-relaxed ${sub}`}>{overall.why}</p>
      {overall.provisional && !blocked && (
        <p className="mt-1 text-xs text-amber-700" data-testid="overall-provisional-note">* Provisional — important information is still missing, so this result may change.</p>
      )}
    </div>
  );
}

function Component({ label, weight, value, dark }) {
  return (
    <div className={`rounded-md px-2 py-1.5 ${dark ? "bg-white/10" : "bg-white/70 border border-slate-200"}`}>
      <p className={`text-[10px] uppercase tracking-wide ${dark ? "text-slate-300" : "text-slate-400"}`}>{label} · {weight}</p>
      <p className={`font-mono-num font-bold text-sm ${dark ? "text-white" : "text-slate-800"}`}>{value != null ? `${value}%` : "—"}</p>
    </div>
  );
}

function FitSummaryPanel({ summary }) {
  if (!summary || ((summary.fits || []).length === 0 && (summary.attention || []).length === 0)) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="fit-summary-panel">
      <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-3">Why it fits · what to check</p>
      {(summary.fits || []).length > 0 && (
        <div data-testid="fit-summary-fits">
          <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle weight="fill" className="text-emerald-500" /> Why it fits</p>
          <ul className="mt-1.5 space-y-1.5 mb-4">
            {summary.fits.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" /><span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(summary.attention || []).length > 0 && (
        <div data-testid="fit-summary-attention">
          <p className="text-sm font-semibold text-amber-700 flex items-center gap-1.5"><WarningCircle weight="fill" className="text-amber-500" /> What doesn't fit / needs attention</p>
          <ul className="mt-1.5 space-y-1.5">
            {summary.attention.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" /><span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ReportDialog({ jobId }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("job");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Please tell us what's wrong"); return; }
    setBusy(true);
    try {
      await api.post("/report", { target_type: type, target_id: jobId, reason: reason.trim(), details: details.trim() || null });
      toast.success("Thank you — our team has been alerted.");
      setOpen(false); setReason(""); setDetails(""); setType("job");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="report-open-btn" className="mt-1 flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-red-600">
          <Flag weight="bold" /> Report this listing
        </button>
      </DialogTrigger>
      <DialogContent data-testid="report-dialog">
        <DialogHeader><DialogTitle>Report a problem</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">What are you reporting?</label>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="report-type-select"
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
              <option value="job">Misleading or wrong listing</option>
              <option value="employer">Problem with the employer</option>
              <option value="scam">Scam / fraud</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">What's wrong?</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="report-reason-input"
              placeholder="e.g. Says remote but requires UK residence" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} data-testid="report-details-input" placeholder="Any extra detail (optional)" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="report-submit-btn" className="bg-slate-900 hover:bg-slate-800">Send report</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REQ_TYPE_STYLE = {
  mandatory: "border-red-200 bg-red-50 text-red-700",
  preferred: "border-blue-200 bg-blue-50 text-blue-700",
  flexible: "border-emerald-200 bg-emerald-50 text-emerald-700",
  unknown: "border-slate-200 bg-slate-50 text-slate-500",
};
function ReqTypeBadge({ type }) {
  if (!type) return null;
  const cls = REQ_TYPE_STYLE[type] || REQ_TYPE_STYLE.unknown;
  return (
    <span data-testid={`req-type-${type}`} className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {type}
    </span>
  );
}
